import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { authGuard } from "./auth.guard";

const routes: Routes = [
  {
    path: "login",
    loadComponent: () =>
      import("./pages/login/login.component").then((m) => m.LoginComponent),
  },
  { path: "", redirectTo: "/dashboard", pathMatch: "full" },
  {
    path: "user-guide",
    loadComponent: () =>
      import("./components/user-guide/user-guide.component").then(
        (m) => m.UserGuideComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "dashboard",
    loadComponent: () =>
      import("./pages/dashboard/dashboard.component").then(
        (m) => m.DashboardComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "users",
    loadComponent: () =>
      import("./pages/users/users.component").then((m) => m.UsersComponent),
    canActivate: [authGuard],
  },
  {
    path: "users/:id/audit",
    loadComponent: () =>
      import("./pages/users/user-audit-page/user-audit-page.component").then(
        (m) => m.UserAuditPageComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "courses",
    loadComponent: () =>
      import("./pages/courses/courses.component").then((m) => m.CoursesComponent),
    canActivate: [authGuard],
    children: [
      {
        path: "new",
        loadComponent: () =>
          import("./pages/courses/course-form-dialog/course-form-dialog.component").then(
            (m) => m.CourseFormDialogComponent,
          ),
      },
      {
        path: ":id/edit",
        loadComponent: () =>
          import("./pages/courses/course-form-dialog/course-form-dialog.component").then(
            (m) => m.CourseFormDialogComponent,
          ),
      },
      {
        path: ":id/schedules",
        loadComponent: () =>
          import(
            "./pages/courses/course-schedules-view-dialog/course-schedules-view-dialog.component"
          ).then((m) => m.CourseSchedulesViewDialogComponent),
      },
    ],
  },
  {
    path: "violations",
    loadComponent: () =>
      import("./pages/violations/violations.component").then(
        (m) => m.ViolationsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "enrollments",
    loadComponent: () =>
      import("./pages/enrollments/enrollments.component").then(
        (m) => m.EnrollmentsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "locations",
    loadComponent: () =>
      import("./pages/locations/locations.component").then(
        (m) => m.LocationsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "payment-methods",
    loadComponent: () =>
      import("./pages/payment-methods/payment-methods.component").then(
        (m) => m.PaymentMethodsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "serial-numbers",
    loadComponent: () =>
      import("./pages/serial-numbers/serial-numbers.component").then(
        (m) => m.SerialNumbersComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "payments",
    loadComponent: () =>
      import("./pages/payments/payments.component").then(
        (m) => m.PaymentsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "class-management",
    loadComponent: () =>
      import("./pages/class-management/class-management.component").then(
        (m) => m.ClassManagementComponent,
      ),
    canActivate: [authGuard],
    children: [
      { path: "", redirectTo: "in-person", pathMatch: "full" },
      {
        path: "in-person",
        loadComponent: () =>
          import(
            "./pages/class-management/class-management-in-person-tab/class-management-in-person-tab.component"
          ).then((m) => m.ClassManagementInPersonTabComponent),
      },
      {
        path: "online",
        loadComponent: () =>
          import(
            "./pages/class-management/class-management-online-tab/class-management-online-tab.component"
          ).then((m) => m.ClassManagementOnlineTabComponent),
        children: [
          {
            path: "import",
            loadComponent: () =>
              import(
                "./pages/class-management/class-management-online-import-dialog/class-management-online-import-dialog.component"
              ).then((m) => m.ClassManagementOnlineImportDialogComponent),
          },
        ],
      },
    ],
  },
  {
    path: "course-completed",
    loadComponent: () =>
      import("./pages/course-completed/course-completed.component").then(
        (m) => m.CourseCompletedComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "communications/templates",
    loadComponent: () =>
      import(
        "./pages/communications/email-templates/email-templates.component"
      ).then((m) => m.EmailTemplatesComponent),
    canActivate: [authGuard],
  },
  {
    path: "communications/letter-templates",
    loadComponent: () =>
      import(
        "./pages/communications/letter-templates/letter-templates.component"
      ).then((m) => m.LetterTemplatesComponent),
    canActivate: [authGuard],
  },
  {
    path: "communications/emails",
    loadComponent: () =>
      import("./pages/communications/communications.component").then(
        (m) => m.CommunicationsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "communications/letters",
    loadComponent: () =>
      import("./pages/communications/communications.component").then(
        (m) => m.CommunicationsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "communications",
    redirectTo: "/communications/emails",
    pathMatch: "full",
  },
  {
    path: "communications/auto-trigger",
    loadComponent: () =>
      import("./pages/communications/communications.component").then(
        (m) => m.CommunicationsComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: "communications/auto-trigger/:id",
    loadComponent: () =>
      import(
        "./pages/communications/reminder-configurations/reminder-configuration-dialog/reminder-configuration-dialog.component"
      ).then((m) => m.ReminderConfigurationDialogComponent),
    canActivate: [authGuard],
  },
  {
    path: "communication-history",
    loadComponent: () =>
      import(
        "./pages/violations/communication-history/communication-history.component"
      ).then((m) => m.CommunicationHistoryComponent),
    canActivate: [authGuard],
  },
  {
    path: "violations/respondent-audit",
    loadComponent: () =>
      import(
        "./pages/violations/respondent-audit/respondent-audit-page.component"
      ).then((m) => m.RespondentAuditPageComponent),
    canActivate: [authGuard],
  },
  {
    path: "violations/import-history",
    loadComponent: () =>
      import(
        "./pages/violations/import-history-page/import-history-page.component"
      ).then((m) => m.ImportHistoryPageComponent),
    canActivate: [authGuard],
  },
  {
    path: "settings",
    loadComponent: () =>
      import("./pages/settings/settings.component").then(
        (m) => m.SettingsComponent
      ),
    canActivate: [authGuard],
    data: { roles: ["superadmin"] },
    children: [
      { path: "", redirectTo: "reschedule-configuration", pathMatch: "full" },
      {
        path: "lead-management",
        loadComponent: () =>
          import(
            "./pages/settings/lead-management/lead-management.component"
          ).then((m) => m.LeadManagementComponent),
        canActivate: [authGuard],
        data: { roles: ["superadmin"] },
      },
      {
        path: "theme",
        loadComponent: () =>
          import("./pages/settings/theme-shell/theme-shell.component").then(
            (m) => m.ThemeShellComponent
          ),
        canActivate: [authGuard],
        data: { roles: ["superadmin"] },
        children: [
          { path: "", redirectTo: "admin", pathMatch: "full" },
          {
            path: "admin",
            loadComponent: () =>
              import(
                "./pages/settings/theme-shell/admin-portal-theme/admin-portal-theme.component"
              ).then((m) => m.AdminPortalThemeComponent),
            canActivate: [authGuard],
            data: { roles: ["superadmin"] },
          },
          {
            path: "students",
            loadComponent: () =>
              import(
                "./pages/settings/theme-shell/students-portal-theme/students-portal-theme.component"
              ).then((m) => m.StudentsPortalThemeComponent),
            canActivate: [authGuard],
            data: { roles: ["superadmin"] },
          },
        ],
      },
      {
        path: "branding",
        loadComponent: () =>
          import(
            "./pages/settings/branding-shell/branding-shell.component"
          ).then((m) => m.BrandingShellComponent),
        canActivate: [authGuard],
        data: { roles: ["superadmin"] },
        children: [
          { path: "", redirectTo: "admin", pathMatch: "full" },
          {
            path: "admin",
            loadComponent: () =>
              import(
                "./pages/settings/branding-shell/admin-portal-branding/admin-portal-branding.component"
              ).then((m) => m.AdminPortalBrandingComponent),
            canActivate: [authGuard],
            data: { roles: ["superadmin"] },
          },
          {
            path: "students",
            loadComponent: () =>
              import(
                "./pages/settings/branding-shell/students-portal-branding/students-portal-branding.component"
              ).then((m) => m.StudentsPortalBrandingComponent),
            canActivate: [authGuard],
            data: { roles: ["superadmin"] },
          },
        ],
      },
      {
        path: "customer-theme",
        redirectTo: "theme/students",
        pathMatch: "full",
      },
      {
        path: "reschedule-configuration",
        loadComponent: () =>
          import(
            "./pages/settings/reschedule-config/reschedule-config.component"
          ).then((m) => m.RescheduleConfigComponent),
        canActivate: [authGuard],
        data: { roles: ["superadmin"] },
      },
      {
        path: "faqs",
        loadComponent: () =>
          import("./pages/settings/faqs/faqs.component").then(
            (m) => m.FaqsComponent
          ),
        canActivate: [authGuard],
        data: { roles: ["superadmin"] },
      },
    ],
  },
  {
    path: "reports",
    loadComponent: () =>
      import("./pages/reports/reports.component").then(
        (m) => m.ReportsComponent
      ),
    canActivate: [authGuard],
    children: [
      { path: "", redirectTo: "court-report", pathMatch: "full" },
      {
        path: "court-report",
        loadComponent: () =>
          import("./pages/reports/court-report/court-report.component").then(
            (m) => m.CourtReportComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: "payment-report",
        loadComponent: () =>
          import(
            "./pages/reports/payment-report/payment-report.component"
          ).then((m) => m.PaymentReportComponent),
        canActivate: [authGuard],
      },
    ],
  },
  { path: "**", redirectTo: "/dashboard" },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Required so idle-restore can remount the current page and reload data
      onSameUrlNavigation: "reload",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
